import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface StaffQuotationSummary {
    quotationNumber: string;
    customerId?: string;
    customerName?: string;
    companyId?: string;
    divisionId?: string;
    warehouse?: string;
    status: string;
    lineCount?: number;
    createdAt?: string;
    punchedOrderNumber?: string;
}

export interface StaffQuotationLine {
    id?: number;
    lineNo: number;
    itemNumber: string;
    itemName: string;
    qty: number;
    rate?: number;
    lineAmount?: number;
}

export interface StaffQuotationStatusEvent {
    oldStatus?: string;
    newStatus: string;
    comment?: string;
    changedBy?: string;
    changedAt?: string;
}

export interface StaffQuotationDetail {
    quotationNumber: string;
    status: string;
    customerId?: string;
    customerName?: string;
    companyId?: string;
    divisionId?: string;
    warehouse?: string;
    lineCount?: number;
    createdBy?: string;
    createdAt?: string;
    updatedAt?: string;
    submittedAt?: string;
    punchedOrderNumber?: string;
    punchedAt?: string;
    punchedBy?: string;
    lines: StaffQuotationLine[];
    timeline: StaffQuotationStatusEvent[];
}

@Injectable({ providedIn: 'root' })
export class StaffQuotationService {
    private readonly baseUrl = `${environment.apiUrl}staff/quotations`;

    constructor(private readonly http: HttpClient) {}

    listQuotations(
        scope: { companyId: string; divisionId: string; whsId: string },
        status?: string
    ): Observable<StaffQuotationSummary[]> {
        let params = new HttpParams()
            .set('companyId', scope.companyId)
            .set('divisionId', scope.divisionId)
            .set('whsId', scope.whsId);
        if (status && status !== 'ALL') {
            params = params.set('status', status);
        }
        return this.http.get<StaffQuotationSummary[]>(this.baseUrl, { params });
    }

    getQuotation(
        quotationNumber: string,
        scope: { companyId: string; divisionId: string; whsId: string }
    ): Observable<StaffQuotationDetail> {
        const params = new HttpParams()
            .set('companyId', scope.companyId)
            .set('divisionId', scope.divisionId)
            .set('whsId', scope.whsId);
        return this.http.get<StaffQuotationDetail>(
            `${this.baseUrl}/${encodeURIComponent(quotationNumber)}`,
            { params }
        );
    }

    updateStatus(
        quotationNumber: string,
        scope: { companyId: string; divisionId: string; whsId: string },
        payload: { status: string; customerMessage?: string; internalComment?: string }
    ): Observable<StaffQuotationDetail> {
        const params = new HttpParams()
            .set('companyId', scope.companyId)
            .set('divisionId', scope.divisionId)
            .set('whsId', scope.whsId);
        return this.http.post<StaffQuotationDetail>(
            `${this.baseUrl}/${encodeURIComponent(quotationNumber)}/status`,
            payload,
            { params }
        );
    }

    linkPunchedOrder(
        quotationNumber: string,
        scope: { companyId: string; divisionId: string; whsId: string },
        payload: { punchedOrderNumber: string }
    ): Observable<StaffQuotationDetail> {
        const params = new HttpParams()
            .set('companyId', scope.companyId)
            .set('divisionId', scope.divisionId)
            .set('whsId', scope.whsId);
        return this.http.post<StaffQuotationDetail>(
            `${this.baseUrl}/${encodeURIComponent(quotationNumber)}/punch`,
            payload,
            { params }
        );
    }
}
